import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useStorage } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { STORAGE_KEY } from '../../constants';
import type { StorageVotes } from '../../types';
import { ViewType } from '../../types';
import { useVoteContext } from '../providers';
import { groupVotesById } from '../utils';
import { AnswerButton } from './answer-button';
import { VotesCount } from './votes-count';

export function AnswersContainer() {
  const { editMode, accentColor, textColor, answers, language, resultView } =
    useSettings();

  const {
    viewType,
    data: { total, votes },
    isVoting,
    vote,
    unvote,
    changeVote
  } = useVoteContext();

  const [storedValue] = useStorage<StorageVotes | undefined>(
    STORAGE_KEY,
    undefined
  );

  const [activeId, setActiveId] = useState(
    editMode ? undefined : storedValue?.id
  );

  const groupedVotes = groupVotesById(votes);

  const handleVote = (id: string) => {
    const isStoredIdExisted = votes.find(
      ({ id: voteId }) => storedValue?.id === voteId
    );

    if (isVoting) {
      return;
    }

    if (activeId === id) {
      setActiveId(undefined);

      unvote(id);
      return;
    }

    setActiveId(id);

    if (!activeId || (storedValue?.id && !isStoredIdExisted)) {
      vote(id);
      return;
    }

    changeVote(activeId, id);
  };

  useEffect(() => {
    if (!editMode) {
      return;
    }

    if (viewType === ViewType.VoteView && activeId) {
      unvote(activeId);
      setActiveId(undefined);
    }
  }, [editMode, viewType]);

  return (
    <Container>
      <Wrapper>
        {answers.map(({ id, answer }) => {
          const groupedVote = groupedVotes[id];

          if (!groupedVote || groupedVote.length === 0) {
            return null;
          }

          return (
            <AnswerButton
              key={id}
              id={id}
              showVotes={viewType === ViewType.ResultView}
              data={groupedVote[0]}
              active={id === activeId}
              answer={answer}
              accentColor={accentColor}
              textColor={textColor}
              language={language?.language}
              resultView={resultView}
              onClick={() => handleVote(id)}
            />
          );
        })}
      </Wrapper>

      <VotesCount>{total}</VotesCount>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;
  gap: inherit;
`;
